import { type AppPreload_visitor$key } from '#app/__generated__/relay/AppPreload_visitor.graphql.js';
import { type AppPreloadQuery } from '#app/__generated__/relay/AppPreloadQuery.graphql.js';
import { type CurrentUser$key } from '#app/__generated__/relay/CurrentUser.graphql.js';
import { AppPreloadContext } from '#app/features/app/contexts/AppPreloadContext.js';
import { logger } from '#app/services/logger.js';
import { appPreloadQuery } from './AppPreloadQuery.js';
import { currentUserFragment } from './CurrentUser.fragment.js';
import { type FeatureFlags } from '@contra/feature-flags';
import { VisitorAdvertisementCampaign } from '@contra/graphql-types';
import {
  graphql,
  type PreloadedQuery,
  useFragment,
  usePreloadedQuery,
} from '@contra/relay';
import { setUser as setSentryUser } from '@contra/sentry/react';
import { fromGlobalId } from '@contra/utilities/fromGlobalId';
import { type ReactNode, useEffect, useMemo } from 'react';

type AppPreloadProps = {
  readonly children?: ReactNode;
  readonly preloadedQuery: PreloadedQuery<AppPreloadQuery>;
};

const log = logger.child({
  namespace: 'app:preload',
});

export const AppPreload = ({ children, preloadedQuery }: AppPreloadProps) => {
  const preloadData = usePreloadedQuery<AppPreloadQuery>(
    appPreloadQuery,
    preloadedQuery,
  );

  const visitor = useFragment<AppPreload_visitor$key>(
    graphql`
      fragment AppPreload_visitor on Visitor {
        id
        sessionId
        traits
        featureFlags
        impersonator {
          id
        }
        userAccount {
          id
          ...CurrentUser
        }
        ipAddress {
          id
          address
          autonomousSystem {
            id
            type
          }
        }
        advertisementCampaigns {
          campaign
          locations
        }
      }
    `,
    preloadData.visitor,
  );

  const userAccount = useFragment<CurrentUser$key>(
    currentUserFragment,
    visitor.userAccount,
  );

  /**
   * This effect is run anytime the currentUser changes.
   * We then set Sentry to track the user as needed.
   */
  useEffect(() => {
    if (userAccount) {
      const sentryUser = {
        id: fromGlobalId(userAccount.id).id,
      };

      log.debug({ sentryUser }, 'Setting user for Sentry');
      setSentryUser(sentryUser);
    }
  }, [userAccount]);

  const advertisementCampaigns = useMemo(
    () =>
      visitor.advertisementCampaigns
        ? visitor.advertisementCampaigns.filter(
            (campaign): campaign is VisitorAdvertisementCampaign => {
              return campaign.campaign !== '%future added value';
            },
          )
        : null,
    [visitor.advertisementCampaigns],
  );

  const context = useMemo(() => {
    return {
      advertisementCampaigns,
      currentUser: userAccount ?? null,
      featureFlags: visitor.featureFlags as FeatureFlags,
      impersonator: visitor.impersonator,
      ipAddress: visitor.ipAddress,
      sessionId: visitor.sessionId,
      visitorTraits: visitor.traits as object,
    };
  }, [advertisementCampaigns, userAccount, visitor]);

  return (
    <AppPreloadContext.Provider value={context}>
      {children}
    </AppPreloadContext.Provider>
  );
};
